import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Card, Badge, Row, Col } from 'react-bootstrap';
import { apiRequest } from '../utils/api';
import { BuildingWithConvertedAddresses } from '../utils/types';
import PropertyImageUpload from '../common/PropertyImageUpload';
import { CloseIcon } from '../common/CloseIcon';
import { useTranslation } from 'react-i18next';
import './AdminPrefilledListings.scss';

interface PrefilledListing {
  _id: string;
  building_id: string;
  address: any;
  property_description?: string;
  could_sell: boolean;
  could_buy: boolean;
  prefilled_at: string;
  active_tokens: number;
  latest_token?: {
    expires_at: string;
    intended_email?: string;
  };
}

interface Props {
  isAdmin: boolean;
  selectedBuilding?: BuildingWithConvertedAddresses;
}

const AdminPrefilledListings: React.FC<Props> = ({ isAdmin, selectedBuilding }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [listings, setListings] = useState<PrefilledListing[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    property_description: '',
    property_size: '',
    property_rooms: '',
    property_floor: '',
    property_has_elevator: false,
    property_has_balcony: false,
    property_renovation_status: '',
    property_images: [] as any[],
    could_sell: true,
    could_buy: false,
    intended_email: '',
    expires_in_days: 30,
    max_uses: 1
  });
  
  const [claimUrl, setClaimUrl] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState(false);

  useEffect(() => {
    if (isAdmin && showModal) {
      fetchPrefilledListings();
    }
  }, [isAdmin, showModal]);

  const fetchPrefilledListings = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/api/admin/listings/prefilled', {
        method: 'GET'
      });
      
      if (response.success) {
        setListings(response.listings);
      }
    } catch (err: any) {
      setError('Failed to fetch prefilled listings');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePrefilled = async () => {
    if (!selectedBuilding) {
      setError('Please select a building first');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      const response = await apiRequest('/api/admin/listings/prefill', {
        method: 'POST',
        body: {
          selected_building: selectedBuilding,
          ...formData
        }
      });
      
      if (response.success) {
        setSuccess('Prefilled listing created successfully!');
        setClaimUrl(response.claim_url);
        
        // Reset form
        setFormData({
          property_description: '',
          property_size: '',
          property_rooms: '',
          property_floor: '',
          property_has_elevator: false,
          property_has_balcony: false,
          property_renovation_status: '',
          property_images: [],
          could_sell: true,
          could_buy: false,
          intended_email: '',
          expires_in_days: 30,
          max_uses: 1
        });
        
        // Refresh listings
        fetchPrefilledListings();
      }
    } catch (err: any) {
      setError(err.error || 'Failed to create prefilled listing');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUrl = async () => {
    if (!claimUrl) return;
    try {
      // Prefer modern clipboard API when available
      if (navigator && (navigator as any).clipboard && typeof (navigator as any).clipboard.writeText === 'function') {
        await (navigator as any).clipboard.writeText(claimUrl);
      } else {
        // Fallback for non-secure contexts: hidden textarea + execCommand
        const textarea = document.createElement('textarea');
        textarea.value = claimUrl;
        textarea.readOnly = true;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        textarea.style.pointerEvents = 'none';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch (err) {
      // Last-resort fallback: select text in the input for manual copy
      setCopiedUrl(false);
      setError('Could not copy to clipboard. Please copy the URL manually.');
    }
  };

  const handleRevokeToken = async (listingId: string, tokenId: string) => {
    if (!window.confirm('Are you sure you want to revoke this token?')) {
      return;
    }

    try {
      const response = await apiRequest(`/api/admin/listings/${listingId}/revoke-token`, {
        method: 'POST',
        body: { token_id: tokenId }
      });
      
      if (response.success) {
        setSuccess('Token revoked successfully');
        fetchPrefilledListings();
      }
    } catch (err: any) {
      setError('Failed to revoke token');
    }
  };

  const handleRegenerateToken = async (listingId: string) => {
    try {
      const response = await apiRequest(`/api/admin/listings/${listingId}/prefill-token`, {
        method: 'POST',
        body: {
          expires_in_days: 30,
          max_uses: 1
        }
      });
      
      if (response.success) {
        setClaimUrl(response.claim_url);
        setSuccess('New token generated successfully');
        fetchPrefilledListings();
      }
    } catch (err: any) {
      setError('Failed to regenerate token');
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <>
      <Button 
        variant="warning" 
        onClick={() => setShowModal(true)}
        className="admin-prefilled-button"
      >
        Admin: Prefilled Listings
      </Button>

      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)}
        size="lg"
        className="admin-prefilled-modal"
      >
        <Modal.Header>
          <Modal.Title>Admin: Prefilled Listings</Modal.Title>
          <Button
            variant="link"
            onClick={() => setShowModal(false)}
            className="close-button"
          >
            <CloseIcon size={24} />
          </Button>
        </Modal.Header>

        <Modal.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}
          
          {claimUrl && (
            <Alert variant="info" className="claim-url-alert">
              <strong>Claim URL:</strong>
              <div className="url-container">
                <input 
                  type="text" 
                  value={claimUrl} 
                  readOnly 
                  className="form-control"
                />
                <Button 
                  variant="primary" 
                  onClick={handleCopyUrl}
                  className="copy-button"
                >
                  {copiedUrl ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </Alert>
          )}

          {selectedBuilding && (
            <Card className="mb-3">
              <Card.Body>
                <h5>Create Prefilled Listing</h5>
                <p className="text-muted">
                  Building: {selectedBuilding.addresses?.[0]?.street} {selectedBuilding.addresses?.[0]?.houseNumber}, {selectedBuilding.addresses?.[0]?.city}
                </p>

                <Form>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Listing Type</Form.Label>
                        <Form.Check
                          type="checkbox"
                          label="Could Sell"
                          checked={formData.could_sell}
                          onChange={(e) => setFormData({ ...formData, could_sell: e.target.checked })}
                        />
                        <Form.Check
                          type="checkbox"
                          label="Could Buy"
                          checked={formData.could_buy}
                          onChange={(e) => setFormData({ ...formData, could_buy: e.target.checked })}
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Intended Email (Optional)</Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="recipient@example.com"
                          value={formData.intended_email}
                          onChange={(e) => setFormData({ ...formData, intended_email: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Property Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={formData.property_description}
                      onChange={(e) => setFormData({ ...formData, property_description: e.target.value })}
                      placeholder="Describe the property..."
                    />
                  </Form.Group>

                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Size (m²)</Form.Label>
                        <Form.Control
                          type="number"
                          value={formData.property_size}
                          onChange={(e) => setFormData({ ...formData, property_size: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Rooms</Form.Label>
                        <Form.Control as="select" 
                          value={formData.property_rooms}
                          onChange={(e) => setFormData({ ...formData, property_rooms: e.target.value })}
                        >
                          <option value="">Select</option>
                          <option value="1">1</option>
                          <option value="2">2</option>
                          <option value="3">3</option>
                          <option value="4">4</option>
                          <option value="5+">5+</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Floor</Form.Label>
                        <Form.Control
                          type="number"
                          value={formData.property_floor}
                          onChange={(e) => setFormData({ ...formData, property_floor: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Renovation Status</Form.Label>
                        <Form.Control as="select" 
                          value={formData.property_renovation_status}
                          onChange={(e) => setFormData({ ...formData, property_renovation_status: e.target.value })}
                        >
                          <option value="">Select</option>
                          <option value="recently_renovated">Recently Renovated</option>
                          <option value="good_condition">Good Condition</option>
                          <option value="needs_renovation">Needs Renovation</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Has Elevator"
                      checked={formData.property_has_elevator}
                      onChange={(e) => setFormData({ ...formData, property_has_elevator: e.target.checked })}
                    />
                    <Form.Check
                      type="checkbox"
                      label="Has Balcony"
                      checked={formData.property_has_balcony}
                      onChange={(e) => setFormData({ ...formData, property_has_balcony: e.target.checked })}
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Token Expiry (days)</Form.Label>
                        <Form.Control
                          type="number"
                          value={formData.expires_in_days}
                          onChange={(e) => setFormData({ ...formData, expires_in_days: parseInt(e.target.value) })}
                          min="1"
                          max="365"
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Max Uses</Form.Label>
                        <Form.Control
                          type="number"
                          value={formData.max_uses}
                          onChange={(e) => setFormData({ ...formData, max_uses: parseInt(e.target.value) })}
                          min="1"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {formData.could_sell && (
                    <Form.Group className="mb-3">
                      <Form.Label>Property Images</Form.Label>
                      <PropertyImageUpload
                        images={formData.property_images as any}
                        setImages={(update: any) => {
                          setFormData(prev => {
                            const nextImages = typeof update === 'function' ? update(prev.property_images || []) : update;
                            return { ...prev, property_images: nextImages };
                          });
                        }}
                        maxImages={30}
                        enhancementStatus={null}
                        isLoggedIn={true}
                      />
                    </Form.Group>
                  )}

                  <Button 
                    variant="primary" 
                    onClick={handleCreatePrefilled}
                    disabled={loading || (!formData.could_sell && !formData.could_buy)}
                  >
                    {loading ? <Spinner animation="border" size="sm" /> : 'Create Prefilled Listing'}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          )}

          <h5 className="mt-4">Existing Prefilled Listings</h5>
          
          {loading && !listings.length ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <div className="listings-grid">
              {listings.map((listing) => (
                <Card key={listing._id} className="mb-3">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <strong>{listing.address?.street} {listing.address?.houseNumber}</strong>
                        <div className="text-muted">{listing.address?.city}</div>
                      </div>
                      <Badge variant={listing.active_tokens > 0 ? 'success' : 'secondary'}>
                        {listing.active_tokens} Active Token{listing.active_tokens !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                    
                    <div className="mb-2">
                      {listing.could_sell && <Badge variant="primary" className="me-2">Could Sell</Badge>}
                      {listing.could_buy && <Badge variant="info">Could Buy</Badge>}
                    </div>
                    
                    {listing.property_description && (
                      <p className="text-muted small">{listing.property_description}</p>
                    )}
                    
                    {listing.latest_token && (
                      <div className="small text-muted">
                        Expires: {new Date(listing.latest_token.expires_at).toLocaleDateString()}
                        {listing.latest_token.intended_email && (
                          <div>For: {listing.latest_token.intended_email}</div>
                        )}
                      </div>
                    )}
                    
                    <div className="mt-2">
                      <Button 
                        size="sm" 
                        variant="outline-primary" 
                        onClick={() => handleRegenerateToken(listing._id)}
                        className="me-2"
                      >
                        Generate New Token
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              ))}
              
              {!listings.length && (
                <p className="text-muted">No prefilled listings yet</p>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AdminPrefilledListings;

